// ThemeContext.js
// React context providing dark & light theme colors for FoutaLib
// --------------------------------------------------------------
// Usage:
//   import { ThemeProvider, useTheme } from "./ThemeContext";
//   <ThemeProvider>
//      <App />
//   </ThemeProvider>
//   const { theme, toggleTheme } = useTheme();

import React, { createContext, useContext, useState, useEffect } from "react";

// ------------------
// Theme Color Tokens
// ------------------
const lightTheme = {
  name: "light",
  className: "theme-light",
  colors: {
    primary: "#D4AF37",
    primaryHover: "#b8962f",
    primaryShadow: "#e8d296",
    background: "#FAFAFA",
    cardBackground: "#FFFFFF",
    navBackground: "#FFFFFF",
    footerBackground: "#F5F5F5",
    sectionAlternate: "#F0F0F0",
    textMain: " #111111",
    textMuted: " #555555",
    textHeading: " #000000",
    link: "#D4AF37",
    linkHover: "#b8962f",
    cardBorder: "#E6E6E6",
    dividerLine: "#DADADA",
    inputBorder: "#CCCCCC",
    success: "#28C76F",
    error: "#EA5455",
    warning: "#FF9F43",
    info: "#00CFE8",
  },
};

const darkTheme = {
  name: "dark",
  className: "theme-dark",
  colors: {
    primary: " #D4AF37",
    primaryHover: " #f5cc62",
    primaryShadow: " #4a3f20",
    background: " #0B0B0F",
    cardBackground: " #16161D",
    navBackground: " #0E0E13",
    footerBackground: " #101018",
    sectionAlternate: " #1C1C26",
    textMain: " #F0F0F5",
    textMuted: " #A1A1AA",
    textHeading: " #FFFFFF",
    link: " #D4AF37",
    linkHover: " #f5cc62",
    cardBorder: " #2A2A35",
    dividerLine: " #2D2D38",
    inputBorder: " #33334D",
    success: " #28C76F",
    error: " #EA5455",
    warning: " #FF9F43",
    info: " #00CFE8",
  },
};

const ThemeContext = createContext({
  theme: darkTheme,
  toggleTheme: () => {},
});

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(darkTheme); // Default is now darkTheme

  useEffect(() => {
    const stored = localStorage.getItem("theme-mode");
    if (stored === "dark") {
      setTheme(darkTheme);
    } else if (stored === "light") {
      setTheme(lightTheme);
    } else {
      const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      setTheme(systemPrefersDark ? darkTheme : darkTheme); // Always default to dark
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;

    // Transition animation
    root.classList.add("theme-transition");
    setTimeout(() => root.classList.remove("theme-transition"), 300);

    // Apply theme class
    root.classList.remove(lightTheme.className, darkTheme.className);
    root.classList.add(theme.className);

    // Set CSS vars for fallback support
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });

    localStorage.setItem("theme-mode", theme.name);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev.name === "light" ? darkTheme : lightTheme));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
